export const DEFAULT_CURRENCY_CONFIG = Object.freeze({
  currencyCode: 'INR',
  currencyLocale: 'en-IN',
});

export const CURRENCY_OPTIONS = Object.freeze([
  { value: 'INR', label: 'Indian Rupee (INR)', defaultLocale: 'en-IN' },
  { value: 'USD', label: 'US Dollar (USD)', defaultLocale: 'en-US' },
  { value: 'EUR', label: 'Euro (EUR)', defaultLocale: 'de-DE' },
  { value: 'GBP', label: 'British Pound (GBP)', defaultLocale: 'en-GB' },
  { value: 'AED', label: 'UAE Dirham (AED)', defaultLocale: 'ar-AE' },
  { value: 'SGD', label: 'Singapore Dollar (SGD)', defaultLocale: 'en-SG' },
  { value: 'AUD', label: 'Australian Dollar (AUD)', defaultLocale: 'en-AU' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)', defaultLocale: 'en-CA' },
]);

export const LOCALE_OPTIONS = Object.freeze([
  { value: 'en-IN', label: 'English (India)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'ar-AE', label: 'Arabic (UAE)' },
  { value: 'en-SG', label: 'English (Singapore)' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-CA', label: 'English (Canada)' },
]);

const SUPPORTED_CURRENCY_CODES = new Set(CURRENCY_OPTIONS.map((entry) => entry.value));
const SUPPORTED_LOCALES = new Set(LOCALE_OPTIONS.map((entry) => entry.value));
const formatterCache = new Map();
const listeners = new Set();

let currentCurrencyConfig = { ...DEFAULT_CURRENCY_CONFIG };

export function normalizeCurrencyCode(value) {
  const nextValue = String(value || '').trim().toUpperCase();
  return SUPPORTED_CURRENCY_CODES.has(nextValue)
    ? nextValue
    : DEFAULT_CURRENCY_CONFIG.currencyCode;
}

export function normalizeCurrencyLocale(value) {
  const nextValue = String(value || '').trim();
  return SUPPORTED_LOCALES.has(nextValue)
    ? nextValue
    : DEFAULT_CURRENCY_CONFIG.currencyLocale;
}

export function normalizeCurrencyConfig(source = {}) {
  return {
    currencyCode: normalizeCurrencyCode(source.currencyCode),
    currencyLocale: normalizeCurrencyLocale(source.currencyLocale),
    currencyLocked: Boolean(source.currencyLocked),
  };
}

export function resolveCurrencyConfig(source = {}, fallback = DEFAULT_CURRENCY_CONFIG) {
  const normalizedFallback = normalizeCurrencyConfig(fallback);
  return normalizeCurrencyConfig({
    currencyCode: source.currencyCode || normalizedFallback.currencyCode,
    currencyLocale: source.currencyLocale || normalizedFallback.currencyLocale,
    currencyLocked:
      typeof source.currencyLocked === 'boolean'
        ? source.currencyLocked
        : normalizedFallback.currencyLocked,
  });
}

export function getCurrentCurrencyConfig() {
  return currentCurrencyConfig;
}

export function setCurrentCurrencyConfig(source = {}) {
  currentCurrencyConfig = resolveCurrencyConfig(source, DEFAULT_CURRENCY_CONFIG);
  listeners.forEach((listener) => listener());
}

export function subscribeCurrencyConfig(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getFormatter(config = {}, options = {}) {
  const { currencyCode, currencyLocale } = resolveCurrencyConfig(config, currentCurrencyConfig);
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  const style = options.style || 'currency';
  const currencyDisplay = options.currencyDisplay || 'symbol';
  const cacheKey = JSON.stringify({
    currencyCode,
    currencyLocale,
    minimumFractionDigits,
    maximumFractionDigits,
    style,
    currencyDisplay,
  });

  if (!formatterCache.has(cacheKey)) {
    formatterCache.set(
      cacheKey,
      new Intl.NumberFormat(currencyLocale, {
        style,
        ...(style === 'currency'
          ? { currency: currencyCode, currencyDisplay }
          : {}),
        minimumFractionDigits,
        maximumFractionDigits,
      })
    );
  }

  return formatterCache.get(cacheKey);
}

export function formatCurrencyValue(value, config = {}, options = {}) {
  const amount = Number(value || 0);
  return getFormatter(config, options).format(amount);
}

export function formatNumberValue(value, config = {}, options = {}) {
  const amount = Number(value || 0);
  return getFormatter(config, {
    ...options,
    style: 'decimal',
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
  }).format(amount);
}

export function getCurrencySymbol(config = {}) {
  const { currencyCode, currencyLocale } = resolveCurrencyConfig(config, currentCurrencyConfig);
  try {
    const parts = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: currencyCode,
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value || currencyCode;
  } catch (_error) {
    return currencyCode;
  }
}

export function getCurrencyOption(value) {
  return CURRENCY_OPTIONS.find((entry) => entry.value === value) || null;
}
